import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "services/api";
import {
  AdminCancelTemporaryAccessResult,
  AdminTemporaryAccessMode,
  AdminTemporaryAccessResult,
  PublicTemporaryAccessState,
  TemporaryAccessRequest,
  TemporaryAccessStatus,
} from "types/temporaryAccess.types";
import type { DealerInfoResponse } from "./useDealers";

interface ApiEnvelope<T> {
  success: boolean;
  response: T;
}

/** Prefix key; invalidating it refreshes every dealer-scoped entry below. */
const temporaryAccessQueryKey = ["temporary-dealer-access-requests"] as const;

/**
 * Scoped by dealer so a cached list from a previous session (the QueryClient
 * outlives logout) is never shown to, or counted against, another dealer.
 */
export const useTemporaryDealerAccessRequests = (
  dealerId: number | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: [...temporaryAccessQueryKey, dealerId],
    enabled: enabled && dealerId !== undefined,
    queryFn: async (): Promise<TemporaryAccessRequest[]> => {
      const { data } = await api.get<ApiEnvelope<TemporaryAccessRequest[]>>(
        "dpp/dealer/temporary-access-requests",
      );
      return data.response;
    },
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

export const useCreateTemporaryDealerAccessRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (serialNumber: string): Promise<TemporaryAccessRequest> => {
      const { data } = await api.post<ApiEnvelope<TemporaryAccessRequest>>(
        "dpp/dealer/temporary-access-requests",
        { serialNumber },
      );
      return data.response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: temporaryAccessQueryKey });
      queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
  });
};

export const useCancelPendingTemporaryAccessRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      requestId: number,
    ): Promise<{ id: number; status: TemporaryAccessStatus; cancelledAt: string }> => {
      const { data } = await api.delete<
        ApiEnvelope<{ id: number; status: TemporaryAccessStatus; cancelledAt: string }>
      >(`dpp/dealer/temporary-access-requests/${requestId}`);
      return data.response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: temporaryAccessQueryKey });
      queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
  });
};

/**
 * Admin creates temporary access for a dealer on a device.
 * mode "grant" = immediate 72h access, no email; "request" = customer email.
 */
export const useAdminCreateTemporaryDealerAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      dealerId,
      serialNumber,
      mode,
    }: {
      dealerId: number;
      serialNumber: string;
      mode: AdminTemporaryAccessMode;
    }): Promise<AdminTemporaryAccessResult> => {
      const { data } = await api.post<ApiEnvelope<AdminTemporaryAccessResult>>(
        // "dpp/": served by dpp-backend admin-api; the bare "admin/" base path
        // still maps to the legacy dpp-admin-service, which lacks this route.
        `dpp/admin/dealer/${dealerId}/temporary-access`,
        { serialNumber, mode },
      );
      return data.response;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["dealerInfo", String(variables.dealerId)],
      });
    },
  });
};

/**
 * Admin cancels a dealer's pending request or ends their active access
 * (X on the Dealer Details temporary access cards).
 */
export const useAdminCancelTemporaryDealerAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      dealerId,
      requestId,
    }: {
      dealerId: number;
      requestId: number;
    }): Promise<AdminCancelTemporaryAccessResult> => {
      const { data } = await api.delete<
        ApiEnvelope<AdminCancelTemporaryAccessResult>
      >(`dpp/admin/dealer/${dealerId}/temporary-access/${requestId}`);
      return data.response;
    },
    onSuccess: (_data, { dealerId, requestId }) => {
      // Drop the card right away; the refetch below confirms the list.
      queryClient.setQueryData<DealerInfoResponse>(
        ["dealerInfo", String(dealerId)],
        (current) =>
          current && {
            ...current,
            temporaryAccess: current.temporaryAccess?.filter(
              (access) => access.id !== requestId,
            ),
          },
      );
    },
    // A 404/409 also means the page is out of date, so refetch either way.
    onSettled: (_data, _error, { dealerId }) => {
      queryClient.invalidateQueries({
        queryKey: ["dealerInfo", String(dealerId)],
      });
    },
  });
};

const usePublicTemporaryAccessState = (
  path: "decision" | "cancel",
  token: string,
  enabled = true,
) =>
  useQuery({
    queryKey: ["temporary-access", path, token],
    enabled: enabled && Boolean(token),
    retry: false,
    queryFn: async (): Promise<PublicTemporaryAccessState> => {
      const { data } = await api.get<ApiEnvelope<PublicTemporaryAccessState>>(
        `dpp/temporary-access/${path}`,
        { params: { token } },
      );
      return data.response;
    },
  });

export const usePublicTemporaryAccessDecision = (
  token: string,
  enabled = true,
) => usePublicTemporaryAccessState("decision", token, enabled);

export const usePublicTemporaryAccessCancel = (
  token: string,
  enabled = true,
) => usePublicTemporaryAccessState("cancel", token, enabled);

export const useSubmitTemporaryAccessDecision = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      token,
      decision,
    }: {
      token: string;
      decision: "approve" | "reject";
    }): Promise<{ status: TemporaryAccessStatus; accessExpiresAt?: string }> => {
      const { data } = await api.post<
        ApiEnvelope<{ status: TemporaryAccessStatus; accessExpiresAt?: string }>
      >("dpp/temporary-access/decision", { token, decision });
      return data.response;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["temporary-access", "decision", variables.token],
      });
    },
  });
};

export const useSubmitTemporaryAccessCancel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (token: string): Promise<{ status: TemporaryAccessStatus }> => {
      const { data } = await api.post<ApiEnvelope<{ status: TemporaryAccessStatus }>>(
        "dpp/temporary-access/cancel",
        { token },
      );
      return data.response;
    },
    onSuccess: (_data, token) => {
      queryClient.invalidateQueries({
        queryKey: ["temporary-access", "cancel", token],
      });
      queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
  });
};
